import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/cognivault_logo.png';
import { cleanApiUrl } from '../lib/api';

const features = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
      </svg>
    ),
    title: 'Centralized Repository',
    desc: 'One authoritative source for all official records — resolutions, ordinances, meeting minutes, maps, and more. SHA-256 checksummed. Non-editable. PDF/A preserved.'
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
      </svg>
    ),
    title: 'Natural Language Search',
    desc: 'Ask questions in plain English across your entire document corpus and structured databases. No SQL. No forms. Just answers with citations.'
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
      </svg>
    ),
    title: 'AI-Powered Redaction',
    desc: 'Intelligently redact sensitive content before public release. On-premises LLM — no data leaves your environment. Original preserved, clean version published.'
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0"/>
      </svg>
    ),
    title: 'Public Transparency Portal',
    desc: 'A branded citizen-facing portal for searching and viewing published records. No login required. WCAG 2.1 AA compliant. English and Spanish.'
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7z"/><path d="M9 12l2 2 4-4"/>
      </svg>
    ),
    title: 'NC DNCR Compliance',
    desc: 'Tamper-evident audit trail. Automatic destroy-after dates. Legal holds. Designed for public records compliance and transparency.'
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
      </svg>
    ),
    title: 'Legacy Migration',
    desc: 'Full migration from OpenText Alchemy and 17 legacy formats including TIF, OCR, DAT, PRO, and proprietary index files. Metadata preserved. Searchable from day one.'
  },
];

const stats = [
  { value: '21,500+', label: 'Documents migrated' },
  { value: '< 500ms', label: 'Search response time' },
  { value: '99.5%', label: 'Uptime SLA' },
  { value: '100%', label: 'On-premises AI' },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', org: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await Promise.all([
        fetch('https://formspree.io/f/xojpwwad', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }),
        fetch(`${cleanApiUrl()}/demo-requests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }),
      ]);
      setSubmitted(true);
    } catch (err) {
      alert('Failed to send message. Please try again.');
    }
  };  

  return (
    <div style={{ fontFamily: "'Georgia', 'Times New Roman', serif", background: '#0A0F1E', minHeight: '100vh', color: '#E8EDF5' }}>

      {/* ── Nav ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '0 2rem',
        height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: scrolled ? 'rgba(10,15,30,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '4px 10px', display: 'flex', alignItems: 'center' }}>
            <img src={logo} alt="CogniVault" style={{ height: '28px', width: 'auto', objectFit: 'contain' }} />
          </div>
          <span style={{ color: 'rgba(232,237,245,0.4)', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.05em' }}>by CogniAI Inc.</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          <a href="#features" style={{ color: 'rgba(232,237,245,0.7)', fontSize: '14px', textDecoration: 'none', fontFamily: 'sans-serif' }}>Features</a>
          <a href="#contact" style={{ color: 'rgba(232,237,245,0.7)', fontSize: '14px', textDecoration: 'none', fontFamily: 'sans-serif' }}>Contact</a>
          <button
            onClick={() => navigate('/demo')}
            style={{
              background: '#2563EB', color: '#fff', border: 'none',
              padding: '8px 20px', borderRadius: '6px',
              fontSize: '14px', fontFamily: 'sans-serif',
              cursor: 'pointer', fontWeight: '500',
            }}>
            Request demo
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '120px 2rem 80px',
        textAlign: 'center',
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(37,99,235,0.15) 0%, transparent 70%)',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Grid background */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 0,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(37,99,235,0.15)',
            border: '1px solid rgba(37,99,235,0.3)',
            borderRadius: '20px', padding: '6px 16px',
            fontSize: '13px', fontFamily: 'sans-serif',
            color: '#93C5FD', marginBottom: '32px', letterSpacing: '0.05em',
          }}>
            A product of CogniAI Inc.
          </div>
          <div style={{ display: 'block', textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ display: 'inline-block', background: 'white', borderRadius: '16px', padding: '16px 32px', boxShadow: '0 0 60px rgba(37,99,235,0.25)' }}>
            <img src={logo} alt="CogniVault" style={{ height: '64px', width: 'auto', objectFit: 'contain', display: 'block' }} />
            </div>
          </div>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
            fontWeight: '400', lineHeight: '1.1',
            marginBottom: '24px', letterSpacing: '-0.02em',
            color: '#F0F4FF',
          }}>
            Government records,<br />
            <span style={{ color: '#60A5FA', fontStyle: 'italic' }}>intelligently managed.</span>
          </h1>
          <p style={{
            fontSize: '1.2rem', lineHeight: '1.7',
            color: 'rgba(232,237,245,0.65)',
            maxWidth: '560px', margin: '0 auto 48px',
            fontFamily: 'sans-serif', fontWeight: '400',
          }}>
            CogniVault is a COTS-configured digital records repository and public search platform built for municipal governments — with AI-powered search, on-premises LLM, and full legacy migration.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/demo')}
              style={{
                background: '#2563EB', color: '#fff',
                border: 'none', padding: '14px 32px',
                borderRadius: '8px', fontSize: '16px',
                fontFamily: 'sans-serif', fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 0 40px rgba(37,99,235,0.4)',
              }}>
              Access live demo
            </button>
            <a href="#features" style={{
              background: 'transparent', color: '#E8EDF5',
              border: '1px solid rgba(255,255,255,0.15)',
              padding: '14px 32px', borderRadius: '8px',
              fontSize: '16px', fontFamily: 'sans-serif',
              fontWeight: '500', cursor: 'pointer',
              textDecoration: 'none', display: 'inline-block',
            }}>
              See features
            </a>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section style={{
        padding: '60px 2rem',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.02)',
      }}>
        <div style={{
          maxWidth: '900px', margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '40px', textAlign: 'center',
        }}>
          {stats.map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: '2.5rem', fontWeight: '300', color: '#60A5FA', letterSpacing: '-0.02em' }}>{s.value}</div>
              <div style={{ fontSize: '13px', color: 'rgba(232,237,245,0.5)', marginTop: '6px', fontFamily: 'sans-serif', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" style={{ padding: '100px 2rem', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: '400', color: '#F0F4FF', marginBottom: '16px', letterSpacing: '-0.02em' }}>
            Everything a modern records platform needs
          </h2>
          <p style={{ fontSize: '1.1rem', color: 'rgba(232,237,245,0.55)', fontFamily: 'sans-serif', maxWidth: '500px', margin: '0 auto' }}>
            Built for modern municipal government. Deployed in 30 days.
          </p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px',
        }}>
          {features.map((f, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: '12px', padding: '32px',
              transition: 'border-color 0.2s, background 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(37,99,235,0.4)'; e.currentTarget.style.background = 'rgba(37,99,235,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
            >
              <div style={{ color: '#60A5FA', marginBottom: '16px' }}>{f.icon}</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '400', color: '#F0F4FF', marginBottom: '10px', fontFamily: 'sans-serif' }}>{f.title}</h3>
              <p style={{ fontSize: '14px', color: 'rgba(232,237,245,0.55)', lineHeight: '1.7', fontFamily: 'sans-serif' }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section style={{
        margin: '0 2rem 80px',
        maxWidth: '900px',
        marginLeft: 'auto', marginRight: 'auto',
        background: 'linear-gradient(135deg, rgba(37,99,235,0.2) 0%, rgba(15,118,110,0.2) 100%)',
        border: '1px solid rgba(37,99,235,0.3)',
        borderRadius: '16px', padding: '60px 48px',
        textAlign: 'center',
      }}>
        <h2 style={{ fontSize: '2rem', fontWeight: '400', color: '#F0F4FF', marginBottom: '16px', letterSpacing: '-0.02em' }}>
          See CogniVault in action
        </h2>
        <p style={{ fontSize: '1rem', color: 'rgba(232,237,245,0.6)', marginBottom: '32px', fontFamily: 'sans-serif' }}>
          Access the live demo — search real records, ask natural language questions,<br />and explore the public portal.
        </p>
        <button
          onClick={() => navigate('/demo')}
          style={{
            background: '#2563EB', color: '#fff',
            border: 'none', padding: '14px 40px',
            borderRadius: '8px', fontSize: '16px',
            fontFamily: 'sans-serif', fontWeight: '500',
            cursor: 'pointer',
          }}>
          Access live demo →
        </button>
      </section>

      {/* ── Contact ── */}
      <section id="contact" style={{
        padding: '80px 2rem',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.01)',
      }}>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: '400', color: '#F0F4FF', marginBottom: '12px', letterSpacing: '-0.02em' }}>Get in touch</h2>
            <p style={{ fontSize: '1rem', color: 'rgba(232,237,245,0.55)', fontFamily: 'sans-serif' }}>
              Interested in CogniVault for your municipality? We'd love to talk.
            </p>
          </div>
          {submitted ? (
            <div style={{
              background: 'rgba(15,118,110,0.15)',
              border: '1px solid rgba(15,118,110,0.3)',
              borderRadius: '12px', padding: '40px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px' }}>✓</div>
              <div style={{ color: '#5DCAA5', fontSize: '1.1rem', fontFamily: 'sans-serif' }}>Message received — we'll be in touch shortly.</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { key: 'name', label: 'Full name', type: 'text', placeholder: 'Jane Smith' },
                { key: 'org', label: 'Organization', type: 'text', placeholder: 'City of ...' },
                { key: 'email', label: 'Email address', type: 'email', placeholder: 'jane@city.gov' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: '13px', color: 'rgba(232,237,245,0.6)', marginBottom: '6px', fontFamily: 'sans-serif', letterSpacing: '0.03em' }}>{f.label}</label>
                  <input
                    type={f.type} placeholder={f.placeholder} required
                    value={formData[f.key]}
                    onChange={e => setFormData({ ...formData, [f.key]: e.target.value })}
                    style={{
                      width: '100%', padding: '12px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px', color: '#E8EDF5',
                      fontSize: '15px', fontFamily: 'sans-serif',
                      outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>
              ))}
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: 'rgba(232,237,245,0.6)', marginBottom: '6px', fontFamily: 'sans-serif', letterSpacing: '0.03em' }}>Message</label>
                <textarea
                  rows={4} placeholder="Tell us about your records management needs..."
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  style={{
                    width: '100%', padding: '12px 16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px', color: '#E8EDF5',
                    fontSize: '15px', fontFamily: 'sans-serif',
                    outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                  }}
                />
              </div>
              <button type="submit" style={{
                background: '#2563EB', color: '#fff',
                border: 'none', padding: '14px',
                borderRadius: '8px', fontSize: '15px',
                fontFamily: 'sans-serif', fontWeight: '500',
                cursor: 'pointer', marginTop: '8px',
              }}>
                Send message
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── CogniAI Company Section ── */}
      <section style={{
        padding: '60px 2rem',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        maxWidth: '900px', margin: '0 auto',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '48px',
      }}>
        <div>
          <div style={{
            display: 'inline-block',
            background: 'rgba(37,99,235,0.15)',
            border: '1px solid rgba(37,99,235,0.25)',
            borderRadius: '8px', padding: '6px 14px',
            fontSize: '13px', color: '#93C5FD',
            fontFamily: 'sans-serif', marginBottom: '16px',
            letterSpacing: '0.05em',
          }}>
            CogniAI Inc.
          </div>
          <p style={{ color: 'rgba(232,237,245,0.5)', fontSize: '14px', fontFamily: 'sans-serif', lineHeight: '1.7', margin: 0 }}>
            Formerly Fracsys Inc. Over a decade of government technology delivery. Leesburg, Virginia.
          </p>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'rgba(232,237,245,0.3)', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Certifications</div>
          {[
            'NC HUB Certified · HUB-402345',
            'Minority Owned Business',
            'Women Owned Small Business (W/SBE)',
            'Pega Certified Partner',
          ].map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#60A5FA', flexShrink: 0 }} />
              <span style={{ color: 'rgba(232,237,245,0.55)', fontSize: '13px', fontFamily: 'sans-serif' }}>{c}</span>
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: '12px', color: 'rgba(232,237,245,0.3)', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>Contact</div>
          {[
            '880 Harrison St SE, Suite 054',
            'Leesburg, Virginia 20175',
            'info@cogniai.com',
          ].map((c, i) => (
            <div key={i} style={{ color: 'rgba(232,237,245,0.55)', fontSize: '13px', fontFamily: 'sans-serif', marginBottom: '6px' }}>{c}</div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{
        padding: '32px 2rem',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ background: 'white', borderRadius: '6px', padding: '3px 8px' }}>
            <img src={logo} alt="CogniVault" style={{ height: '22px', width: 'auto', objectFit: 'contain', display: 'block' }} />
          </div>
        </div>
        <div style={{ fontSize: '13px', color: 'rgba(232,237,245,0.35)', fontFamily: 'sans-serif' }}>
          © 2026 CogniAI Inc. · Modern Government Records Management
        </div>
      </footer>

    </div>
  );
}
